import copyToClipboard from "@insite/client-framework/Common/Utilities/copyToClipboard";
import translate from "@insite/client-framework/Translate";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import AsyncAxiomIcon from "@insite/shell/Components/Icons/AxiomIcon/AsyncAxiomIcon";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import styled, { css } from "styled-components";

interface Props {
    timestamp: string;
    cfRay?: string;
    datadogTraceId?: string;
}

const Container = styled.div`
    border-top: 1px solid ${getColor("common.border")};
    margin: 30px -30px 0;
    padding: 20px 30px 0;
    font-family: monospace;
    font-size: 12px;
    user-select: text;
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
`;

const CopyIcon = styled.span`
    display: inline-flex;
    margin-right: 5px;
`;

const ErrorModalTimestamp: FC<Props> = ({ timestamp, cfRay, datadogTraceId }) => {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setCopied(false);
    }, [timestamp, cfRay, datadogTraceId]);

    const timestampLabel = translate("Timestamp:");
    const cloudflareRayIdLabel = translate("Cloudflare Ray ID");
    const datadogTraceIdLabel = translate("Datadog Trace ID (lower 64 bits)");

    const copyHandler = async () => {
        const lines = [`${timestampLabel} ${timestamp}`];
        if (cfRay) {
            lines.push(`${cloudflareRayIdLabel}: ${cfRay}`);
        }
        if (datadogTraceId) {
            lines.push(`${datadogTraceIdLabel}: ${datadogTraceId}`);
        }

        if (await copyToClipboard(lines.join("\n"))) {
            setCopied(true);
        }
    };

    return (
        <Container data-test-selector="unhandledErrorModal_errorReferenceId">
            <Header>
                <Typography as="p">
                    {translate("Please send the following information to support for investigation.")}
                </Typography>
                <Link
                    css={css`
                        white-space: nowrap;
                    `}
                    onClick={copyHandler}
                    data-test-selector="unhandledErrorModalCopyTimestamp"
                >
                    <CopyIcon>
                        <AsyncAxiomIcon src={copied ? "check" : "copy"} size={14} color="text.link" />
                    </CopyIcon>
                    {copied ? translate("Copied!") : translate("Copy")}
                </Link>
            </Header>
            <Typography as="p" data-test-selector="unhandledErrorModalTimestamp">
                {`${timestampLabel} ${timestamp}`}
            </Typography>
            {cfRay && <Typography as="p">{`${cloudflareRayIdLabel}: ${cfRay}`}</Typography>}
            {datadogTraceId && <Typography as="p">{`${datadogTraceIdLabel}: ${datadogTraceId}`}</Typography>}
        </Container>
    );
};

export default ErrorModalTimestamp;
